import { forwardRef, useState, useImperativeHandle, type ReactNode } from 'react'
import {
  Tbody,
  Tr,
  Td,
  Checkbox,
  Badge,
  HStack,
  Image,
  Box,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Divider,
} from '@chakra-ui/react'
import { MoreVertical, Edit, Trash2, RotateCcw, Download, ArrowRight } from 'lucide-react'
import { Link as RouterLink } from 'react-router-dom'
import type { DataTableOptions, Permission } from './types'
import { useThemeColors } from '@/hooks/useThemeColors'

interface TableBodyProps {
  options: DataTableOptions
  rows: any[]
  permissions?: Permission
  onAction?: (id: any, action: string) => void
  onCheckedAll?: (checked: boolean) => void
  customRenderers?: Record<string, (value: any, row?: any) => ReactNode>
  onRowClick?: (row: any) => void
}

export interface TableBodyRef {
  checkedAll: (isCheckedAll: boolean) => void
  getSelectedData: () => number[]
}

export const TableBody = forwardRef<TableBodyRef, TableBodyProps>(
  ({ options, rows, permissions, onAction, onCheckedAll, customRenderers, onRowClick }, ref) => {
    const { dataFields, primaryKey = 'id', dataSource, softDelete, displayInModal, otherActions } = options
    const hasPermission = permissions

    const [selectedData, setSelectedData] = useState<number[]>([])
    const [isCheckedAll, setIsCheckedAll] = useState(false)

    const handleCheckboxChange = (rowId: number, checked: boolean) => {
      const updatedSelectedData = checked ? [...selectedData, rowId] : selectedData.filter((id) => id !== rowId)
      setSelectedData(updatedSelectedData)

      if (updatedSelectedData.length === rows.length && rows.length > 0) {
        setIsCheckedAll(true)
        onCheckedAll?.(true)
      } else {
        setIsCheckedAll(false)
        onCheckedAll?.(false)
      }
    }

    const handleCheckAll = (checked: boolean) => {
      const allSelected = checked ? rows.map((row) => row[primaryKey]) : []
      setSelectedData(allSelected)
      setIsCheckedAll(checked)
    }

    useImperativeHandle(ref, () => ({
      checkedAll: handleCheckAll,
      getSelectedData: () => selectedData,
    }))

    const renderActions = (id: any, row: any, delDate: any, attachment?: string) => {
      const menuItems: JSX.Element[] = []
      const canEdit = hasPermission?.can_edit && (!hasPermission?.canEditRow || hasPermission.canEditRow(row))
      const canDelete = hasPermission?.can_delete && (!hasPermission?.canDeleteRow || hasPermission.canDeleteRow(row))

      if (!delDate && canEdit) {
        if (displayInModal) {
          menuItems.push(
            <MenuItem
              key="edit"
              icon={<Edit size={16} />}
              onClick={(e) => {
                e.stopPropagation()
                onAction?.(id, 'Edit')
              }}
            >
              Edit
            </MenuItem>
          )
        } else {
          menuItems.push(
            <MenuItem
              key="edit"
              as={RouterLink}
              to={`${dataSource}/${id}`}
              icon={<Edit size={16} />}
              onClick={(e) => e.stopPropagation()}
            >
              Edit
            </MenuItem>
          )
        }
      }

      if (attachment && !delDate) {
        menuItems.push(
          <MenuItem
            key="download"
            icon={<Download size={16} />}
            as="a"
            href={attachment}
            target="_blank"
            download
            onClick={(e) => e.stopPropagation()}
          >
            Download
          </MenuItem>
        )
      }

      if (Array.isArray(otherActions) && !delDate) {
        otherActions
          .filter((action) => !action.show || action.show(row))
          .forEach((action, index) => {
            if (action.onClick) {
              menuItems.push(
                <MenuItem
                  key={`other-${index}`}
                  icon={<ArrowRight size={16} />}
                  onClick={(e) => {
                    e.stopPropagation()
                    action.onClick?.(row)
                  }}
                >
                  {action.name}
                </MenuItem>
              )
            } else if (action.link) {
              menuItems.push(
                <MenuItem
                  key={`other-${index}`}
                  as={RouterLink}
                  to={`${action.link}/${id}`}
                  icon={<ArrowRight size={16} />}
                  onClick={(e) => e.stopPropagation()}
                >
                  {action.name}
                </MenuItem>
              )
            }
          })
      }

      if (menuItems.length > 0 && (canDelete || delDate)) {
        menuItems.push(<Divider key="divider-1" />)
      }

      if (delDate && canDelete) {
        menuItems.push(
          <MenuItem
            key="restore"
            icon={<RotateCcw size={16} />}
            color="green.600"
            onClick={(e) => {
              e.stopPropagation()
              onAction?.(id, 'Restore')
            }}
          >
            Restore
          </MenuItem>
        )
      }

      if (softDelete && !delDate && canDelete) {
        menuItems.push(
          <MenuItem
            key="trash"
            icon={<Trash2 size={16} />}
            color="orange.600"
            onClick={(e) => {
              e.stopPropagation()
              onAction?.(id, 'Trash')
            }}
          >
            Trash
          </MenuItem>
        )
      }

      if (softDelete && delDate && canDelete) {
        menuItems.push(
          <MenuItem
            key="delete"
            icon={<Trash2 size={16} />}
            color="red.600"
            onClick={(e) => {
              e.stopPropagation()
              onAction?.(id, 'Delete')
            }}
          >
            Delete Permanently
          </MenuItem>
        )
      }

      if (menuItems.length === 0) return <Box width="100%" />

      return (
        <Menu>
          <MenuButton
            as={IconButton}
            aria-label="Actions"
            icon={<MoreVertical size={18} />}
            variant="ghost"
            size="sm"
            onClick={(e) => e.stopPropagation()}
          />
          <MenuList minW="180px" onClick={(e) => e.stopPropagation()}>
            {menuItems}
          </MenuList>
        </Menu>
      )
    }

    const colors = useThemeColors()
    const rowHoverBg = colors.sectionBg
    const rowStripeBg = 'rgba(0, 0, 0, 0.05)'
    const emptyTextColor = colors.textSecondary
    const breachedRowBg = 'rgba(239, 68, 68, 0.10)'
    const breachedRowText = 'red.600'

    if (rows.length === 0) {
      return (
        <Tbody>
          <Tr>
            <Td colSpan={Object.keys(dataFields).length + 2} textAlign="center" py={8}>
              <Box color={emptyTextColor}>{options.noRecordText || 'No Record Found'}</Box>
            </Td>
          </Tr>
        </Tbody>
      )
    }

    return (
      <Tbody>
        {rows.map((row, rowIndex) => {
          const rowId = row[primaryKey]
          const delDate = row.deleted_at

          return (
            <Tr
              key={rowIndex}
              bg={
                row.sla_breached
                  ? breachedRowBg
                  : rowIndex % 2 === 1
                    ? rowStripeBg
                    : 'transparent'
              }
              color={row.sla_breached ? breachedRowText : undefined}
              _hover={{ bg: rowHoverBg }}
              cursor={onRowClick ? 'pointer' : 'default'}
              onClick={() => onRowClick?.(row)}
            >
              <Td py={2} px={4}>
                <Checkbox
                  isChecked={selectedData.includes(rowId)}
                  onChange={(e) => {
                    e.stopPropagation()
                    handleCheckboxChange(rowId, e.target.checked)
                  }}
                  onClick={(e) => e.stopPropagation()}
                  colorScheme="blue"
                />
              </Td>
              {Object.keys(dataFields).map((field, index) => {
                const columnMaxWidth = dataFields[field].maxWidth || '350px'
                const isFirstColumn = index === 0
                const isDateField =
                  /(_at|date|time)$/i.test(field) || /\b(date|time)\b/i.test(dataFields[field].name || '')

                return (
                  <Td
                    key={field}
                    maxW={columnMaxWidth}
                    className={dataFields[field].className}
                    whiteSpace={isFirstColumn || isDateField ? 'nowrap' : 'normal'}
                    overflow="hidden"
                    textOverflow="ellipsis"
                    py={2}
                    px={4}
                  >
                    {dataFields[field].attachment &&
                      (() => {
                        const attachmentField =
                          typeof dataFields[field].attachment === 'string' ? dataFields[field].attachment : field
                        const imageUrl = row[attachmentField]
                        return imageUrl ? (
                          <Image
                            src={imageUrl}
                            alt={imageUrl || 'Image'}
                            width="60px"
                            height="60px"
                            objectFit="cover"
                            borderRadius="md"
                            fallbackSrc="/assets/img/no-image.svg"
                            mr={2}
                            display="inline-block"
                            verticalAlign="middle"
                          />
                        ) : null
                      })()}
                    {customRenderers && customRenderers[field] ? (
                      customRenderers[field](row[field], row)
                    ) : dataFields[field].badge ? (
                      <Badge colorScheme={dataFields[field].badge[row[field]] || 'gray'} variant="solid">
                        {dataFields[field].badgeLabels
                          ? dataFields[field].badgeLabels[row[field]] || row[field]
                          : row[field]}
                      </Badge>
                    ) : (
                      <span>
                        {field === 'file_size' && row[field]
                          ? (() => {
                              const bytes = typeof row[field] === 'number' ? row[field] : parseInt(row[field])
                              if (isNaN(bytes)) return row[field]
                              const units = ['B', 'KB', 'MB', 'GB', 'TB']
                              let size = bytes
                              let unit = 0
                              while (size >= 1024 && unit < units.length - 1) {
                                size /= 1024
                                unit++
                              }
                              return `${size.toFixed(2)} ${units[unit]}`
                            })()
                          : row[field]}
                      </span>
                    )}
                  </Td>
                )
              })}
              <Td width="80px" minW="80px" whiteSpace="nowrap" textAlign="right" py={2} px={4}>
                {renderActions(rowId, row, delDate)}
              </Td>
            </Tr>
          )
        })}
      </Tbody>
    )
  }
)

TableBody.displayName = 'TableBody'


